import { create } from 'zustand'
import {
    BetChooserStartsClosingMessage,
    GameUIMessageType,
    OpenBetChooserClickedMessage,
    SpinButtonState,
} from 'game-ui-types'

type ActiveTriggerBetSectionButtonLabel = 'betSection' | 'betStepper' | ''

export const DEFAULT_SELECTED_AUTOPLAY_INDEX = -1

export type SpinButtonSectionStore = {
    state: SpinButtonState
    isBetSectionOpened: boolean
    isAutoplaySectionOpened: boolean
    isAutoplayMode: boolean
    autoplayCount: number
    selectedAutoplayIndex: number
    isOverlayOpened: boolean
    isLeftHandMode: boolean
    isLongPress: boolean
    toggleBetSection: () => void
    activeTriggerButtonLabel: ActiveTriggerBetSectionButtonLabel
    setActiveTriggerButtonLabel: (button: ActiveTriggerBetSectionButtonLabel) => void
    toggleAutoplaySection: () => void
    updateAutoplayMode: (hasStarted: boolean) => void
    close: () => void
    playPickersCloseSound: () => void
    updateSelectedAutoplayIndex: (index: number) => void
    updateAutoplayCount: (count: number) => void
    setState: (newState: SpinButtonState) => void
    updateIsLeftHandMode: (isLeftHandMode: boolean) => void
    updateIsLongPress: (isLongPress: boolean) => void
    isBetInProgress: boolean
    updateIsBetInProgress: (isBetInProgress: boolean) => void
    isStopButtonInteractive: boolean
    updateIsStopButtonInteractive: (isInteractive: boolean) => void
    isSpaceKeyPressed?: boolean
    setIsSpaceKeyPressed?: (isPressed: boolean) => void
    isGambleActive: boolean
    setIsGambleActive: (isActive: boolean) => void
    speedButtonLevel: number
    setSpeedButtonLevel: (level: number) => void
}

export const useSpinButtonSectionStore = create<SpinButtonSectionStore>((set) => ({
    state: SpinButtonState.SEND_IDLE_STATE,
    isBetSectionOpened: false,
    isAutoplaySectionOpened: false,
    isAutoplayMode: false,
    autoplayCount: 0,
    selectedAutoplayIndex: DEFAULT_SELECTED_AUTOPLAY_INDEX,
    isLeftHandMode: false,
    isOverlayOpened: false,
    isLongPress: false,
    activeTriggerButtonLabel: '',
    toggleBetSection: () =>
        set((state) => ({ isBetSectionOpened: !state.isBetSectionOpened, isOverlayOpened: !state.isOverlayOpened })),
    setActiveTriggerButtonLabel: (button: ActiveTriggerBetSectionButtonLabel) =>
        set(() => ({ activeTriggerButtonLabel: button })),
    toggleAutoplaySection: () =>
        set((state) => ({
            isAutoplaySectionOpened: !state.isAutoplaySectionOpened,
            isOverlayOpened: !state.isOverlayOpened,
        })),
    updateSelectedAutoplayIndex: (index: number) => set(() => ({ selectedAutoplayIndex: index })),
    updateAutoplayCount: (count: number) => set(() => ({ autoplayCount: count })),
    updateAutoplayMode: (hasStarted: boolean) => {
        const data = { isAutoplayMode: hasStarted }

        // Send autoplay changed to communicator/integrations
        window.postMessage({ event: 'autoplayChanged', data })

        set(data)
    },
    close: () => set(() => ({ isBetSectionOpened: false, isAutoplaySectionOpened: false, isOverlayOpened: false })),
    setState: (newState: SpinButtonState) =>
        set(() => {
            return { state: newState }
        }),
    playPickersCloseSound: () => {
        const openChooserClickedMessage: OpenBetChooserClickedMessage = {
            type: GameUIMessageType.OPEN_BET_CHOOSER_CLICKED,
        }

        window.uiBus.postMessage(openChooserClickedMessage)

        const chooserStartsClosingMessage: BetChooserStartsClosingMessage = {
            type: GameUIMessageType.BET_CHOOSER_STARTS_CLOSING,
        }

        window.uiBus.postMessage(chooserStartsClosingMessage)
    },
    updateIsLeftHandMode: (isLeftHandMode: boolean) => set(() => ({ isLeftHandMode })),
    updateIsLongPress: (isLongPress: boolean) => set(() => ({ isLongPress })),
    isBetInProgress: false,
    updateIsBetInProgress: (isBetInProgress: boolean) => set(() => ({ isBetInProgress })),
    isStopButtonInteractive: true,
    updateIsStopButtonInteractive: (isInteractive: boolean) => set(() => ({ isStopButtonInteractive: isInteractive })),
    isSpaceKeyPressed: false,
    setIsSpaceKeyPressed: (isPressed: boolean) => set(() => ({ isSpaceKeyPressed: isPressed })),
    isGambleActive: false,
    setIsGambleActive: (isActive: boolean) => set(() => ({ isGambleActive: isActive })),
    speedButtonLevel: 0,
    setSpeedButtonLevel: (level: number) => set(() => ({ speedButtonLevel: level })),
}))
